import * as api from "api"
import SeoMetaData from "common/SeoMetaData"
import { StaticPropsFactoryProps } from "common/StaticPropsFactory"
import slugify from "common/utils/slugify"
import FAQComp from "components/FAQ/FAQComp"
import StaticMenu, {
  ScrollingProvider,
  Section,
  STICKY_OFFSET,
} from "components/StaticMenu"
import VehicleCatalogs from "components/Vehicle/VehicleCatalogs"
import VehicleChildren from "components/Vehicle/VehicleChildren"
import VehicleDescription from "components/Vehicle/VehicleDescription"
import VehicleGalleries from "components/Vehicle/VehicleGalleries"
import VehiclePerformancesDesktop from "components/Vehicle/VehiclePerformancesDesktop"
import VehiclePerformancesMobile from "components/Vehicle/VehiclePerformancesMobile"
import VehiclePolution from "components/Vehicle/VehiclePolution"
import VehicleSafetySystems from "components/Vehicle/VehicleSafetySystems"
import VehicleTechnologiesDesktop from "components/Vehicle/VehicleTechnologiesDesktop"
import VehicleTechnologiesMobile from "components/Vehicle/VehicleTechnologiesMobile"
import VehicleTiles from "components/Vehicle/VehicleTiles"
import { GetStaticPaths, GetStaticProps } from "next"
import Shared from "theme/shared"

type ModelStaticProps = { modelId: string; modelSlug: string }

export const getStaticPaths: GetStaticPaths<ModelStaticProps> = async () => {
  try {
    const { data } = await api.getVehicleModels()
    const paths = data.body.map((model) => ({
      params: {
        modelId: model.id.toString(),
        modelSlug: slugify(model.name),
      },
    }))
    return { paths, fallback: "blocking" }
  } catch (err) {
    console.error(
      "Failed to load vehicle models for static paths:",
      err instanceof Error ? err.message : err,
    )
    return { paths: [], fallback: "blocking" }
  }
}

type ModelProps = StaticPropsFactoryProps & {
  model: api.Vehicle & api.CustomVehicle & api.FVehicle & api.TransitVehicle
}

export const getStaticProps: GetStaticProps<
  ModelProps,
  ModelStaticProps
> = async ({ params }) => {
  let model: api.Vehicle & api.CustomVehicle & api.FVehicle & api.TransitVehicle

  try {
    model = (await api
      .getVehicleId(params.modelId)
      .then(api.extractData)) as api.Vehicle &
      api.CustomVehicle &
      api.FVehicle &
      api.TransitVehicle
  } catch (err) {
    console.error(
      `Failed to load vehicle model ${params.modelId}:`,
      err instanceof Error ? err.message : err,
    )
    return { notFound: true, revalidate: 60 }
  }

  if (!model) {
    return { notFound: true, revalidate: 60 }
  }

  let seo: api.SeoMetaData | null = null
  try {
    seo = await api
      .getSeoKey(encodeURIComponent((model as api.Vehicle)?.name))
      .then(api.extractData)
  } catch (err) {
    console.error(
      `Failed to load SEO for model ${params.modelId}:`,
      err instanceof Error ? err.message : err,
    )
  }

  return { props: { seo, model }, revalidate: 60 }
}

const Model = ({ model, seo }: ModelProps) => {
  const performancesTitle = api.pluralize(
    "מנוע",
    (model as api.Vehicle).vehiclePerformances,
  )

  return (
    <ScrollingProvider offset={STICKY_OFFSET}>
      <SeoMetaData data={seo} />
      <Shared.Mobile aboveTheFold>
        <StaticMenu />
      </Shared.Mobile>

      <Section id="details" title="פרטי רכב">
        {/* פרטי רכב */}
        <VehicleDescription data={model} />
      </Section>

      <Section {...api.Types.getSectionProps(model.familyType)}>
        {/* דגמי בן \ רמות גימור */}
        <VehicleChildren
          type={model.familyType}
          data={model.vehicleChildModels}
          modelId={model.id}
          modelName={model.name}
        />
      </Section>

      <Section id="gallery" title="גלריה">
        {/* גלריה */}
        <VehicleTiles data={model.vehicleTiles} />
      </Section>

      <Section id="attributes" title="מאפייני רכב">
        {/* מאפייני פנים וחוץ */}
        <VehicleGalleries
          data={{
            outOptions: model.vehicleOutOptionsGallery,
            inOptions: model.vehicleInOptionsGallery,
          }}
        />
      </Section>

      {model.vehicleSafetySystems.safetySystems?.length > 0 && (
        <Section id="safety" title="בטיחות">
          {/* בטיחות */}
          <Shared.Desktop>
            <Shared.Divider />
          </Shared.Desktop>
          <VehicleSafetySystems
            data={model.vehicleSafetySystems.safetySystems}
          />
        </Section>
      )}

      {model.vehiclePerformances?.length > 0 && (
        <Section id="performaces" title={performancesTitle}>
          {/* מנוע | מנועים */}
          <Shared.Desktop>
            <VehiclePerformancesDesktop
              title={performancesTitle}
              data={model.vehiclePerformances}
            />
          </Shared.Desktop>
          <Shared.Mobile>
            <VehiclePerformancesMobile
              title={performancesTitle}
              data={model.vehiclePerformances}
            />
          </Shared.Mobile>
        </Section>
      )}

      {model.vehicleTechnologies?.length > 0 && (
        <Section id="technologies" title="טכנולוגיה">
          {/* ביצועים וטכנולוגיה */}
          <Shared.Desktop>
            <Shared.Divider />
            <VehicleTechnologiesDesktop data={model.vehicleTechnologies} />
          </Shared.Desktop>
          <Shared.Mobile>
            <VehicleTechnologiesMobile data={model.vehicleTechnologies} />
          </Shared.Mobile>
        </Section>
      )}

      {model.vehicleCatalogs?.length > 0 && (
        <Section id="catalog" title="קטלוג">
          {/* קטלוג */}
          <Shared.Desktop>
            <Shared.Divider />
          </Shared.Desktop>
          <VehicleCatalogs data={model.vehicleCatalogs} />
        </Section>
      )}

      {model.polutionAndSafetyImages?.length > 0 && (
        <Section id="polution" title="פליטת מזהמים וצריכת דלק">
          {/* פליטת מזהמים וצריכת דלק */}
          <Shared.Desktop>
            <Shared.Divider />
          </Shared.Desktop>
          <VehiclePolution data={model.polutionAndSafetyImages} />
        </Section>
      )}

      {model.faq?.isActive && <FAQComp data={model.faq} isModelPage />}
    </ScrollingProvider>
  )
}

export default Model
